// Role definitions and hierarchy
export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager', 
  DEVELOPER: 'developer',
  TESTER: 'tester',
  VIEWER: 'viewer',
  CLIENT: 'client',
} as const

export type Role = typeof ROLES[keyof typeof ROLES]

// Role hierarchy - higher roles have access to lower role permissions
export const ROLE_HIERARCHY: Record<Role, Role[]> = {
  [ROLES.ADMIN]: [ROLES.ADMIN, ROLES.MANAGER, ROLES.DEVELOPER, ROLES.TESTER, ROLES.VIEWER, ROLES.CLIENT],
  [ROLES.MANAGER]: [ROLES.MANAGER, ROLES.DEVELOPER, ROLES.TESTER, ROLES.VIEWER, ROLES.CLIENT],
  [ROLES.DEVELOPER]: [ROLES.DEVELOPER, ROLES.VIEWER],
  [ROLES.TESTER]: [ROLES.TESTER, ROLES.VIEWER],
  [ROLES.VIEWER]: [ROLES.VIEWER],
  [ROLES.CLIENT]: [ROLES.CLIENT],
}

// Role display names
export const ROLE_DISPLAY_NAMES: Record<Role, string> = {
  [ROLES.ADMIN]: 'Administrator',
  [ROLES.MANAGER]: 'Project Manager',
  [ROLES.DEVELOPER]: 'Developer',
  [ROLES.TESTER]: 'Tester',
  [ROLES.VIEWER]: 'Viewer',
  [ROLES.CLIENT]: 'Client',
}

// Role descriptions
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  [ROLES.ADMIN]: 'Full system access including user management and system settings',
  [ROLES.MANAGER]: 'Can manage projects, assign issues, and view reports',
  [ROLES.DEVELOPER]: 'Can create and update issues, view project details',
  [ROLES.TESTER]: 'Can create and update issues, view project details',
  [ROLES.VIEWER]: 'Read-only access to projects and issues',
  [ROLES.CLIENT]: 'Can view only assigned projects, no time tracking visibility',
}

// Role colors for UI
export const ROLE_COLORS: Record<Role, string> = {
  [ROLES.ADMIN]: 'error',
  [ROLES.MANAGER]: 'warning',
  [ROLES.DEVELOPER]: 'info',
  [ROLES.TESTER]: 'secondary',
  [ROLES.VIEWER]: 'default',
  [ROLES.CLIENT]: 'success',
}

// Permission definitions
export const PERMISSIONS = {
  // User Management
  MANAGE_USERS: 'manage_users',
  VIEW_USERS: 'view_users',
  
  // Project Management
  CREATE_PROJECTS: 'create_projects',
  EDIT_PROJECTS: 'edit_projects',
  DELETE_PROJECTS: 'delete_projects',
  VIEW_PROJECTS: 'view_projects',
  
  // Issue Management
  CREATE_ISSUES: 'create_issues',
  EDIT_ISSUES: 'edit_issues',
  DELETE_ISSUES: 'delete_issues',
  ASSIGN_ISSUES: 'assign_issues',
  VIEW_ISSUES: 'view_issues',
  
  // System Management
  MANAGE_SYSTEM: 'manage_system',
  VIEW_ANALYTICS: 'view_analytics',
  MANAGE_SECURITY: 'manage_security',
  
  // Reports
  VIEW_REPORTS: 'view_reports',
  EXPORT_REPORTS: 'export_reports',
} as const

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS]

// Role permissions mapping
export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  [ROLES.ADMIN]: [
    PERMISSIONS.MANAGE_USERS,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.CREATE_PROJECTS,
    PERMISSIONS.EDIT_PROJECTS,
    PERMISSIONS.DELETE_PROJECTS,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.CREATE_ISSUES,
    PERMISSIONS.EDIT_ISSUES,
    PERMISSIONS.DELETE_ISSUES,
    PERMISSIONS.ASSIGN_ISSUES,
    PERMISSIONS.VIEW_ISSUES,
    PERMISSIONS.MANAGE_SYSTEM,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.MANAGE_SECURITY,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
  ],
  [ROLES.MANAGER]: [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.CREATE_PROJECTS,
    PERMISSIONS.EDIT_PROJECTS,
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.CREATE_ISSUES,
    PERMISSIONS.EDIT_ISSUES,
    PERMISSIONS.ASSIGN_ISSUES,
    PERMISSIONS.VIEW_ISSUES,
    PERMISSIONS.VIEW_ANALYTICS,
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
  ],
  [ROLES.DEVELOPER]: [
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.CREATE_ISSUES,
    PERMISSIONS.EDIT_ISSUES,
    PERMISSIONS.VIEW_ISSUES,
  ],
  [ROLES.TESTER]: [
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.CREATE_ISSUES,
    PERMISSIONS.EDIT_ISSUES,
    PERMISSIONS.VIEW_ISSUES,
  ],
  [ROLES.VIEWER]: [
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_ISSUES,
  ],
  [ROLES.CLIENT]: [
    PERMISSIONS.VIEW_PROJECTS,
    PERMISSIONS.VIEW_ISSUES,
  ],
}

// Utility functions
export const hasPermission = (userRole: Role, permission: Permission): boolean => {
  return ROLE_PERMISSIONS[userRole]?.includes(permission) || false
}

export const hasRole = (userRole: Role, requiredRole: Role): boolean => {
  return ROLE_HIERARCHY[userRole]?.includes(requiredRole) || false
}

export const canManageRole = (userRole: Role, targetRole: Role): boolean => {
  // Users can only manage roles lower than or equal to their own
  const userHierarchy = ROLE_HIERARCHY[userRole] || []
  return userHierarchy.includes(targetRole)
}

export const getAvailableRoles = (userRole: Role): Role[] => {
  return ROLE_HIERARCHY[userRole] || []
}

export const getRoleDisplayName = (role: Role): string => {
  return ROLE_DISPLAY_NAMES[role] || role
}

export const getRoleDescription = (role: Role): string => {
  return ROLE_DESCRIPTIONS[role] || ''
}

export const getRoleColor = (role: Role): string => {
  return ROLE_COLORS[role] || 'default'
}

// Validation functions
export const isValidRole = (role: string): role is Role => {
  return Object.values(ROLES).includes(role as Role)
}

export const validateRoleTransition = (fromRole: Role, toRole: Role): boolean => {
  // Only allow transitions to roles that are lower in hierarchy
  return ROLE_HIERARCHY[fromRole]?.includes(toRole) || false
}
